import { useDisclosure } from "@heroui/react";
import { ModalBuilder, ModalError } from "./../modalBuilder";
import { api } from "@/api";
import { toast } from "react-toastify";
import { useAppSelector } from "@/hooks/useAppSelector.ts";
import { OfficeLocation } from "@/api/locations";
import { useNavigate } from "react-router-dom";
import useErrorHandling from "@/hooks/useErrorHandling";
import { useState } from "react";
import { loadHandbooks } from "@/utils/loadHandbooks";
import { PromptAddressModal } from "./address/promptAddressModal";
import { createLocationFields } from "./createLocationModal";

const editLocationFields = createLocationFields;

export function EditLocationModal({
  disclosure,
  location,
  onLocationUpdated,
}: {
  disclosure: ReturnType<typeof useDisclosure>;
  location: OfficeLocation | null;
  onLocationUpdated?: () => void;
}) {
  const { isOpen, onOpenChange } = disclosure;
  const businessProfile = useAppSelector(
    (state) => state.businessProfile.businessProfile,
  );
  const navigate = useNavigate();
  const [modalErrors, setModalErros] = useState<ModalError[]>([]);
  const [data, setData] = useState<Partial<OfficeLocation>>({});
  const [addresses, setAddresses] = useState<string[]>([]);

  const promptAddressModalDisclosure = useDisclosure();

  const handleError = useErrorHandling();

  const onPromptAddress = (data: Partial<OfficeLocation>) => {
    return new Promise<boolean>((resolve) => {
      api.locations
        .update(location!.id, {
          display_name: data.display_name || "",
          address: data.address || "",
          city_name: data.city_name || "",
          metro_station: data.metro_station || "",
          district: data.district || "",
          business_profile: businessProfile.id,
          // @ts-ignore
          building_type: data.building_type || null,
          latitude: data.latitude ? parseFloat(data.latitude.toString()) : 0,
          longitude: data.longitude ? parseFloat(data.longitude.toString()) : 0,
          // @ts-ignore
          total_area: data.total_area || null,
          // @ts-ignore
          floors_count: data.floors_count || null,
          // @ts-ignore
          construction_year: data.construction_year || null,
          // @ts-ignore
          cadastral_number: data.cadastral_number || null,
        })
        .then(() => {
          promptAddressModalDisclosure.onClose();
          loadHandbooks({ handbooks: ["locations", "cities"] });
          disclosure.onClose();
          onLocationUpdated?.();
          resolve(true);
        })
        .catch((err) => {
          const { errorMessage, parsedErrors } = handleError(err);
          setModalErros(parsedErrors || []);

          toast.error(errorMessage);
          resolve(false);
        });
    });
  };

  const onSubmit = (data: {
    display_name: string;
    address: string;
    city: string;
    metro_station: string;
    district: string;
    building_type: string;
    latitude: string;
    longitude: string;
  }) => {
    return new Promise<boolean>((resolve) => {
      api.locations
        .getAddresses(data.address || "")
        .then((res) => {
          if (res.data.length > 0) {
            disclosure.onClose();
            promptAddressModalDisclosure.onOpen();
            setData({
              ...data,
              latitude: parseFloat(data.latitude) || 0,
              longitude: parseFloat(data.longitude) || 0,
            });
            setAddresses(res.data);
            resolve(false);
          } else {
            onPromptAddress({
              ...data,
              latitude: parseFloat(data.latitude) || 0,
              longitude: parseFloat(data.longitude) || 0,
            })
              .then((res) => {
                resolve(res);
              })
              .catch((err) => {
                const { errorMessage, parsedErrors } = handleError(err);
                setModalErros(parsedErrors || []);
                toast.error(errorMessage);
                resolve(false);
              });
          }
        })
        .catch((err) => {
          const { errorMessage, parsedErrors } = handleError(err);
          setModalErros(parsedErrors || []);
          toast.error(errorMessage);
          resolve(false);
        });
    });
  };

  const onDelete = () => {
    return new Promise<boolean>((resolve) => {
      api.locations
        .delete(location!.id)
        .then(() => {
          disclosure.onClose();
          navigate("/dashboard/");
          resolve(true);
        })
        .catch((err) => {
          console.log(err);
          const { errorMessage, parsedErrors } = handleError(err);
          setModalErros(parsedErrors || []);
          toast.error(errorMessage);
          resolve(false);
        });
    });
  };

  return (
    <>
      <ModalBuilder
        title="Редактирование локации"
        fields={editLocationFields}
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        modalErrors={modalErrors}
        onSubmit={onSubmit}
        onDelete={onDelete}
        isSensitiveData={true}
        submitButtonText="Сохранить"
        defaultValues={{
          display_name: location?.display_name,
          address: location?.address,
          city_name: location?.city_name,
          metro_station: location?.metro_station,
          district: location?.district,
          total_area: location?.total_area,
          building_type: location?.building_type,
          latitude: location?.latitude,
          longitude: location?.longitude,
          floors_count: location?.floors_count,
          construction_year: location?.construction_year,
          cadastral_number: location?.cadastral_number,
        }}
      />
      <PromptAddressModal
        disclosure={promptAddressModalDisclosure}
        data={data}
        addresses={addresses}
        onConfirm={onPromptAddress}
      />
    </>
  );
}
